
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { updateUserProfile } from "@/services/profileService";
import { sanitizeRedirectPath } from '@/utils/auth/sanitizeRedirect';
import {
  challengePhoneMfa,
  enrollPhoneMfa,
  listPhoneFactors,
  unenrollPhoneMfa,
  verifyPhoneMfa,
} from "@/services/nativePhoneAuthService";

const passwordSchema = z
  .object({
    currentPassword: z
      .string()
      .optional(),
    newPassword: z
      .string()
      .min(6, "Password must be at least 6 characters"),
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match",
    path: ["confirmNewPassword"],
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

interface ProfileDataShape {
  id?: string;
  email?: string | null;
  whatsapp_number?: string | null;
  mfa_enabled?: boolean | null;
}

export const SecuritySettings = ({ profileData }: { profileData: ProfileDataShape }) => {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
  const [isPasswordResetFlow, setIsPasswordResetFlow] = useState(false);
  const { t } = useTranslation('common');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAuthSetupFlow = searchParams.get("authSetup") === "true";
  const authSetupRedirectPath = sanitizeRedirectPath(searchParams.get('redirect'));
  const enableWhatsAppMfa = useFeatureFlag("ENABLE_WHATSAPP_MFA");
  const [mfaPhoneNumber, setMfaPhoneNumber] = useState(profileData.whatsapp_number || "");
  const [mfaEnabled, setMfaEnabled] = useState(Boolean(profileData.mfa_enabled));
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [mfaChallengeId, setMfaChallengeId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaInfo, setMfaInfo] = useState<string | null>(null);

  // Check if user came from password reset
  useEffect(() => {
    const hasPasswordResetParam = searchParams.get("passwordReset") === "true";
    const hasPasswordResetToken = searchParams.get("access_token") && searchParams.get("type") === "recovery";
    const hasResetMode = searchParams.get("mode") === "resetPassword";
    
    // Also check the hash for Supabase auth tokens
    const hasHashTokens = location.hash.includes('access_token') && location.hash.includes('type=recovery');
    
    if (hasPasswordResetParam || hasPasswordResetToken || hasResetMode || hasHashTokens) {
      setIsPasswordResetFlow(true);
    }
  }, [location, searchParams]);

  useEffect(() => {
    if (!enableWhatsAppMfa) {
      return;
    }

    let active = true;

    const refreshMfaStatus = async () => {
      setMfaStatusLoading(true);

      try {
        const factors = await listPhoneFactors();
        const verifiedFactor = factors.find((factor) => factor.status === "verified") ?? null;

        if (!active) {
          return;
        }

        setMfaEnabled(Boolean(verifiedFactor));
        setMfaFactorId(verifiedFactor?.id ?? "");
        if (verifiedFactor?.phone) {
          setMfaPhoneNumber(verifiedFactor.phone);
        }
      } catch (caughtError) {
        if (!active) {
          return;
        }

        const message = caughtError instanceof Error ? caughtError.message : "Failed to load MFA status";
        setMfaError(message);
      } finally {
        if (active) {
          setMfaStatusLoading(false);
        }
      }
    };

    void refreshMfaStatus();

    return () => {
      active = false;
    };
  }, [enableWhatsAppMfa]);

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const onSubmit = async (data: PasswordFormValues) => {
    setIsSubmitting(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      const result = await updatePassword(data.newPassword);

      if (!result.success) {
        throw new Error(result.error || "Failed to update password");
      }
      
      setUpdateSuccess(true);
      setIsPasswordResetFlow(false); // Reset the flag after successful update
      form.reset();

      if (isAuthSetupFlow) {
        const destination = authSetupRedirectPath === '/' ? '/' : authSetupRedirectPath;
        setTimeout(() => {
          navigate(destination, { replace: true });
        }, 1000);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update password. Please try again.';
      setUpdateError(message);
      console.error("Failed to update password:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateStoredMfaPreference = async (enabled: boolean) => {
    if (!profileData.id) {
      return;
    }

    await updateUserProfile(profileData.id, { mfa_enabled: enabled });
  };

  const handleEnrollMfa = async (enable: boolean) => {
    if (!enable) {
      if (!mfaFactorId) {
        setMfaEnabled(false);
        return;
      }

      setMfaBusy(true);
      setMfaError(null);
      setMfaInfo(null);

      try {
        const { error } = await unenrollPhoneMfa(mfaFactorId);

        if (error) {
          throw error;
        }

        await updateStoredMfaPreference(false);
        setMfaEnabled(false);
        setMfaFactorId("");
        setMfaChallengeId("");
        setMfaCode("");
        setMfaInfo("Two-factor authentication has been disabled.");
      } catch (caughtError) {
        const message = caughtError instanceof Error ? caughtError.message : "Failed to disable MFA";
        setMfaError(message);
      } finally {
        setMfaBusy(false);
      }

      return;
    }

    setMfaBusy(true);
    setMfaError(null);
    setMfaInfo(null);

    try {
      const { data, error } = await enrollPhoneMfa(mfaPhoneNumber);

      if (error) {
        throw error;
      }

      setMfaFactorId(data.id);
      setMfaEnabled(false);
      setMfaInfo("Enrollment started. Send a code and verify it to enable MFA.");
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Failed to start MFA enrollment";
      setMfaError(message);
    } finally {
      setMfaBusy(false);
    }
  };

  const handleSendMfaCode = async () => {
    if (!mfaFactorId) {
      setMfaError("Start MFA enrollment before requesting a code.");
      return;
    }

    setMfaBusy(true);
    setMfaError(null);
    setMfaInfo(null);

    try {
      const { data, error } = await challengePhoneMfa(mfaFactorId);

      if (error) {
        throw error;
      }

      setMfaChallengeId(data.id);
      setMfaInfo("A verification code has been sent to your WhatsApp number.");
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Failed to send MFA code";
      setMfaError(message);
    } finally {
      setMfaBusy(false);
    }
  };

  const handleVerifyMfaCode = async () => {
    if (!mfaFactorId || !mfaChallengeId || mfaCode.length !== 6) {
      return;
    }

    setMfaBusy(true);
    setMfaError(null);
    setMfaInfo(null);

    try {
      const { error } = await verifyPhoneMfa(mfaFactorId, mfaChallengeId, mfaCode);

      if (error) {
        throw error;
      }

      await updateStoredMfaPreference(true);
      setMfaEnabled(true);
      setMfaChallengeId("");
      setMfaCode("");
      setMfaInfo("Two-factor authentication is now enabled.");
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Failed to verify MFA code";
      setMfaError(message);
    } finally {
      setMfaBusy(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm">
      <div className="border-b border-brand-cloud p-6 md:p-8">
        <h2 className="public-section-title">{t("profile.security.title")}</h2>
        <p className="mt-2 text-sm leading-6 text-brand-slate">{t("profile.security.description")}</p>
      </div>
      <div className="p-6 md:p-8">
        {isAuthSetupFlow && (
          <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-900">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Create a password</AlertTitle>
            <AlertDescription>
              Finish setting up your account by creating a password. After that, you can sign in with email and receive password reset links.
            </AlertDescription>
          </Alert>
        )}

        {updateError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("profile.security.error")}</AlertTitle>
            <AlertDescription>{updateError}</AlertDescription>
          </Alert>
        )}
        
        {updateSuccess && (
          <Alert className="mb-6 border-teal bg-teal-soft text-teal">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{t("profile.security.success")}</AlertDescription>
          </Alert>
        )}

        {isPasswordResetFlow && (
          <Alert className="mb-6 border-harbour bg-harbour-soft text-brand-ink">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Password Reset</AlertTitle>
            <AlertDescription>
              You can set a new password below. No current password required.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {!isPasswordResetFlow && (
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profile.security.currentPassword")}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder={t("profile.security.passwordPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("profile.security.newPassword")}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("profile.security.passwordPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("profile.security.passwordRequirement")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmNewPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("profile.security.confirmPassword")}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t("profile.security.passwordPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("profile.security.updating") : t("profile.security.update")}
            </Button>
          </form>
        </Form>

        {enableWhatsAppMfa && (
          <>
            <Separator className="my-8" />

            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="public-card-title">WhatsApp MFA</h3>
                  <p className="text-sm text-brand-slate">
                    Use Supabase phone MFA with codes delivered through the WhatsApp send hook.
                  </p>
                </div>
                <Switch
                  checked={mfaEnabled}
                  disabled={mfaBusy || mfaStatusLoading || (!mfaEnabled && !mfaPhoneNumber)}
                  onCheckedChange={handleEnrollMfa}
                />
              </div>

              {mfaError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>MFA error</AlertTitle>
                  <AlertDescription>{mfaError}</AlertDescription>
                </Alert>
              )}

              {mfaInfo && (
                <Alert>
                  <AlertTitle>MFA status</AlertTitle>
                  <AlertDescription>{mfaInfo}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="whatsapp-mfa-number">WhatsApp MFA number</Label>
                <Input
                  id="whatsapp-mfa-number"
                  disabled={mfaBusy || mfaEnabled}
                  onChange={(event) => setMfaPhoneNumber(event.target.value)}
                  placeholder="91234567 or +85291234567"
                  value={mfaPhoneNumber}
                />
                <p className="text-sm text-brand-slate">
                  Use the number that can receive WhatsApp messages. Local Hong Kong numbers are normalized to the 852 country code automatically.
                </p>
              </div>

              {!mfaEnabled && mfaFactorId && (
                <div className="space-y-4 rounded-md border border-brand-cloud bg-brand-mist p-4">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button disabled={mfaBusy} onClick={handleSendMfaCode} type="button" variant="outline">
                      {mfaBusy ? "Sending code..." : "Send MFA code"}
                    </Button>
                  </div>

                  {mfaChallengeId && (
                    <div className="space-y-3">
                      <div className="flex justify-center sm:justify-start">
                        <InputOTP maxLength={6} onChange={setMfaCode} value={mfaCode}>
                          <InputOTPGroup className="gap-2">
                            {Array.from({ length: 6 }).map((_, index) => (
                              <InputOTPSlot key={index} index={index} />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>

                      <Button disabled={mfaBusy || mfaCode.length !== 6} onClick={handleVerifyMfaCode} type="button">
                        {mfaBusy ? "Verifying..." : "Verify and enable MFA"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
